import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { rem } from 'polished'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import clsx from 'clsx'
import Image from 'components/Common/Image'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import { EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW } from 'constants/experience'
import NestableClickable from 'components/Common/NestableClickable'
import ExperienceCancellationText from 'components/Experiences/ExperienceCancellationPolicy/ExperienceCancellationText'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { ExperienceBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import config from 'constants/config'
import Typography from 'LuxKit/Typography/Typography'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getImmersiveTripId } from 'tripPlanner/selectors'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { mediaQueryUp, mediaQueryDown } from 'components/utils/breakpoint'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import Group from 'components/utils/Group'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import Pill from 'LuxKit/Pill'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import OfferListEventsContext, {
  OfferListEvents,
} from 'components/OfferList/OfferListEventsContext'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { useOfferInViewTracking } from 'hooks/useOfferInViewTracking'
import ExperienceAppDiscountTag from '../ExperienceAppDiscountTag'
import ExperienceUrgencyTag from './ExperienceUrgencyTag'
import ExperienceTilePricesWithBurn from './ExperienceTilePricesWithBurn'
import { getAgentHubExperienceType } from 'lib/agentHub/agentHubUtils'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import { generateLoyaltyPointsCalculatorExperienceOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import getIsExperienceLoyaltyBurnEnabled from 'loyalty/selectors/getIsExperienceLoyaltyBurnEnabled'
import useExperienceSearchTileBurn from 'components/Experiences/hooks/useExperienceSearchTileBurn'
import NumberRating from 'components/Common/NumberRating'
import { isExperienceOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { getExperienceCompactLocation } from 'lib/experiences/experienceUtils'
import { getImageUrl } from 'lib/image/imageUtils'
import useQueryParams from 'hooks/useQueryParams'
import { queryKeyDate } from 'constants/url'
import Places from 'constants/places'
import { fetchPlaceById } from 'actions/PlaceActions'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import useExperiment from 'hooks/Optimizely/useExperiment'
import { Experiments } from 'constants/optimizely'

const ExperienceImageContainer = styled.div`
  position: relative;
  background-color: ${(props) =>
    props.theme.palette.neutral.surface.lightBackground};
  flex-shrink: 0;
  overflow: hidden;

  ${mediaQueryDown.mobile} {
    width: ${rem(102)};
  }

  ${mediaQueryUp.tablet} {
    height: ${rem(162)};
    width: 100%;
  }
`

const ZOOM_DURATION_MS = 2500

const zoomIn = keyframes`
  from { transform: scale(1); }
  to { transform: scale(1.1); }
`

const ZoomableImageWrapper = styled.div`
  position: absolute;
  inset: 0;

  &.is-animating {
    animation: ${zoomIn} ${ZOOM_DURATION_MS}ms ease-in forwards;
  }
`

const BadgeContainer = styled.div`
  position: absolute;
  top: ${rem(8)};
  right: ${rem(8)};
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${rem(2)};
  z-index: 1;
`

const UrgencyBadgeContainer = styled.div`
  position: absolute;
  bottom: ${rem(8)};
  left: ${rem(8)};
  z-index: 1;
`

const HoverCTA = styled.div`
  display: none;
  flex-shrink: 0;
  gap: ${rem(16)};
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${mediaQueryUp.desktop} {
    display: flex;
  }
`

const PriceSectionLeft = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
`

const Tile = styled(NestableClickable)`
  background-color: ${(props) =>
    props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
  display: flex;
  width: 100%;

  ${mediaQueryDown.mobile} {
    flex-direction: row;
    gap: ${rem(12)};
  }

  ${mediaQueryUp.tablet} {
    flex-direction: column;
    min-height: ${rem(400)};
  }

  &:hover ${HoverCTA} {
    opacity: 1;
  }
`

const TileWrapper = styled.div`
  width: 100%;
`

const TileContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  min-width: 0;

  ${mediaQueryDown.mobile} {
    padding: ${rem(8)} ${rem(12)};
    gap: ${rem(8)};
  }

  ${mediaQueryUp.tablet} {
    padding: ${rem(12)};

    > * + * {
      margin-top: ${rem(8)};
    }
  }
`

interface Props {
  experience: App.ExperienceOffer;
  position?: number;
  date?: string;
  listKey?: string;
}

function ExperienceCompactSearchTile(props: Props) {
  const { experience, position, date, listKey } = props

  const offerMetadata = useAppSelector(state => listKey ? state.experience.experienceOffersMetadata[listKey]?.[experience.id] : undefined)

  const intl = useIntl()
  const dispatch = useAppDispatch()
  const trackEvent = useTrackExperienceOfferEvent()
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const queryParams = useQueryParams()
  const [isHovered, setIsHovered] = useState(false)
  const [previousIndex, setPreviousIndex] = useState(0)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [zoomKey, setZoomKey] = useState(0)
  const hasPreloadedRef = useRef(false)
  const hasTrackedImpressionRef = useRef(false)
  const isMobileDevice = useIsMobileDevice()

  const { setTileRef, trackingInView } = useOfferInViewTracking()

  useEffect(() => {
    if (!trackingInView || position === undefined || hasTrackedImpressionRef.current) return

    hasTrackedImpressionRef.current = true

    dispatchOfferListEvent({
      type: OfferListEvents.productImpression,
      offer: experience,
      position,
      key: EventDataKey.ProductImpression,
    })

    trackEvent('OFFER_TILE_IMPRESSION', [
      {
        type: 'impression',
        data: {
          offer: experience,
        },
      },
    ])
  }, [dispatchOfferListEvent, experience, position, trackEvent, trackingInView])

  const handleMouseEnter = useCallback(() => setIsHovered(true), [])
  const handleMouseLeave = useCallback(() => setIsHovered(false), [])

  useEffect(() => {
    if (!isHovered) {
      hasPreloadedRef.current = false
      return
    }
    if (
      hasPreloadedRef.current ||
      isMobileDevice ||
      experience.images.length <= 1
    ) {
      return
    }
    hasPreloadedRef.current = true

    experience.images.forEach((img) => {
      if (img.id) {
        const preloader = new window.Image()
        preloader.src = getImageUrl(img.id, {
          height: 200,
          dpr: 2,
          format: 'webp',
        })
      }
    })
  }, [isHovered, isMobileDevice, experience.images])

  useEffect(() => {
    if (!isHovered || isMobileDevice || experience.images.length <= 1) return

    const timer = setTimeout(() => {
      setPreviousIndex(currentIndex)
      setCurrentIndex((prev) => (prev + 1) % experience.images.length)
      setZoomKey((prev) => prev + 1)
    }, ZOOM_DURATION_MS)

    return () => clearTimeout(timer)
  }, [
    isHovered,
    currentIndex,
    zoomKey,
    isMobileDevice,
    experience.images.length,
  ])

  useEffect(() => {
    if (isHovered) return
    setPreviousIndex(0)
    setCurrentIndex(0)
    setZoomKey(0)
  }, [isHovered])

  const productType = useMemo(
    () => getAgentHubExperienceType(experience),
    [experience],
  )
  const placeId = queryParams.get('destinationId')
  const place = useAppSelector((state) =>
    placeId ? state.destination.places[placeId] : undefined,
  )
  const isKnownSydneyOrBrisbanePlace = useMemo(() => {
    return Places.Sydney.id === placeId || Places.Brisbane.id === placeId
  }, [placeId])
  useEffect(() => {
    if (!place && !isKnownSydneyOrBrisbanePlace && placeId) {
      dispatch(fetchPlaceById(placeId))
    }
  }, [dispatch, place, isKnownSydneyOrBrisbanePlace, placeId])

  const isLuxPlusEnabled = useAppSelector(
    getIsEnabledLuxPlusDiscountedExperiences,
  )
  const isLoyaltyEnabled = useAppSelector(getIsExperienceLoyaltyBurnEnabled)
  const isLuxPricingShown =
    isLuxPlusEnabled && !!experience.pricing.memberPrice

  const tripId = useAppSelector(getImmersiveTripId)

  const offerPageQueryParams = useMemo(() => {
    const params = new URLSearchParams()
    if (config.OPEN_NEW_TAB_OFFER_CLICK && tripId) {
      params.set('tripId', tripId)
    }
    if (date) {
      params.set(queryKeyDate, date)
    }
    return params.size > 0 ? params : undefined
  }, [tripId, date])

  const handleViewOffer = useCallback(() => {
    if (position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer: experience,
        position,
        key: EventDataKey.ProductClick,
      })
    }

    trackEvent('OFFER_TILE_CLICKED', [
      {
        type: 'click',
        data: {
          offer: experience,
        },
      },
    ])
  }, [experience, dispatchOfferListEvent, position, trackEvent])

  const tileBurn = useExperienceSearchTileBurn({
    totalPrice: experience.price,
    totalMemberPrice: experience.pricing.memberPrice,
    isLuxPlusEnabled,
    isLoyaltyEnabled,
  })

  const pointsEarnCalculationRequests = useMemo<
    Array<App.LoyaltyPointsEarnCalculationRequest | undefined>
  >(
    () => [generateLoyaltyPointsCalculatorExperienceOptions(experience, {
      price: experience.price,
      memberPrice: experience.pricing.memberPrice,
    })],
    [experience],
  )
  const compactLocation = useMemo(
    () => getExperienceCompactLocation(experience.location, 40),
    [experience.location],
  )
  const hasRating =
    config.EXPERIENCES_REVIEWS_ENABLED &&
    isExperienceOfferRatingDisplayable(experience)
  const hasCancellationPolicy = experience.cancellationPolicies.length > 0 && !!experience.copy.cancellationInfo?.refundPolicies?.length
  const { isAnyVariant: shouldShowCancellationPolicy } = useExperiment(
    Experiments.experiencesShowCancellationPolicyOnOfferTiles,
    { canActivate: hasCancellationPolicy },
  )
  const defaultTicketUnitLabel = intl.formatMessage({
    id: 'P3gWPn5epwh5yZWRxHhU',
    defaultMessage: 'total',
    description:
      'Default sale unit label for compact experience search tile pricing',
  })

  return (
    <TileWrapper ref={setTileRef}>
      <Tile
        data-testid="experience-offer-tile"
        to={getExperienceOfferPageURL(experience, offerPageQueryParams)}
        target={config.OPEN_NEW_TAB_OFFER_CLICK ? '_blank' : undefined}
        rel={config.OPEN_NEW_TAB_OFFER_CLICK ? 'noopener noreferrer' : undefined}
        onClick={handleViewOffer}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <ExperienceImageContainer>
          {experience.images[0] && isMobileDevice && (
            <Image
              height={200}
              image={experience.images[0]}
              fit="center"
              dpr={2}
            />
          )}
          {experience.images[0] && !isMobileDevice && (
            <>
              <ZoomableImageWrapper>
                <Image
                  height={200}
                  image={experience.images[previousIndex]}
                  fit="center"
                  dpr={2}
                />
              </ZoomableImageWrapper>
              <ZoomableImageWrapper
                key={zoomKey}
                className={clsx({ 'is-animating': isHovered })}
              >
                <Image
                  height={200}
                  image={experience.images[currentIndex]}
                  fit="center"
                  dpr={2}
                />
              </ZoomableImageWrapper>
            </>
          )}
          <BadgeContainer>
            <TripGuard>
              <ExperienceBookmarkButton
                offer={experience}
                withLabel="never"
                size="small"
              />
            </TripGuard>
          </BadgeContainer>
          <UrgencyBadgeContainer>
            <ExperienceUrgencyTag urgencyTags={offerMetadata?.urgencyTags} experience={experience} />
          </UrgencyBadgeContainer>
        </ExperienceImageContainer>
        <TileContent>
          <VerticalSpacer gap={4}>
            <ExperienceAppDiscountTag experienceOffer={experience} />
            <CSSBreakpoint min="tablet">
              <Group
                direction="horizontal"
                verticalAlign="center"
                horizontalAlign="space-between"
                gap={4}
              >
                <VerticalSpacer gap={2}>
                  <Typography wrap="pretty">
                    {compactLocation && (
                      <Caption as="span" variant="large" weight="normal">
                        {compactLocation}
                      </Caption>
                    )}
                  </Typography>
                </VerticalSpacer>
                {hasRating && (
                  <NumberRating
                    rating={experience.rating?.score}
                    total={experience.rating?.reviewsTotal}
                    variant="xs"
                    numberPosition="start"
                    onlyNumber
                  />
                )}
              </Group>
            </CSSBreakpoint>
            <CSSBreakpoint max="mobile">
              {compactLocation && (
                <BodyText variant="small" colour="neutral-three">
                  {compactLocation}
                </BodyText>
              )}
            </CSSBreakpoint>
            <BodyText title={experience.name} lineClamp={2} variant="large" weight="bold">
              {experience.name}
            </BodyText>
            <CSSBreakpoint max="mobile">
              {hasRating && (
                <NumberRating
                  rating={experience.rating?.score}
                  total={experience.rating?.reviewsTotal}
                  variant="xs"
                  numberPosition="start"
                  onlyNumber
                />
              )}
            </CSSBreakpoint>
            <CSSBreakpoint min="tablet">
              <Group direction="horizontal" gap={8} wrap="wrap">
                <LoyaltyPointsEarn
                  calculationRequests={pointsEarnCalculationRequests}
                  calculationType="estimate"
                  hideStatusCredits
                  pointsToApply={tileBurn.pointsToApplyForEarn}
                  burnPriceInPoints={tileBurn.priceInPointsForEarn}
                />
                {hasCancellationPolicy && shouldShowCancellationPolicy && (
                  <ExperienceCancellationText
                    cancellationInfo={experience.copy.cancellationInfo}
                    textSize="small"
                  />
                )}
              </Group>
            </CSSBreakpoint>
          </VerticalSpacer>
          <div>
            <Group
              direction="horizontal"
              horizontalAlign="space-between"
              verticalAlign="end"
              gap={4}
            >
              <PriceSectionLeft>
                <Group
                  direction="horizontal"
                  verticalAlign="end"
                  gap={8}
                  tabletWrap="wrap"
                >
                  <div>
                    <CSSBreakpoint max="mobile">
                      <ExperienceTilePricesWithBurn
                        tileBurn={tileBurn}
                        totalPrice={experience.price}
                        totalMemberPrice={experience.pricing.memberPrice}
                        isFromPrice
                        ticketUnitLabel={
                          experience.ticketUnitLabel ?? defaultTicketUnitLabel
                        }
                        size="S"
                      />
                    </CSSBreakpoint>
                    <CSSBreakpoint min="tablet">
                      <ExperienceTilePricesWithBurn
                        tileBurn={tileBurn}
                        totalPrice={experience.price}
                        totalMemberPrice={experience.pricing.memberPrice}
                        isFromPrice
                        ticketUnitLabel={
                          experience.ticketUnitLabel ??
                          intl.formatMessage({
                            id: 'P3gWPn5epwh5yZWRxHhU',
                            defaultMessage: 'total',
                            description:
                            'Default sale unit label for compact experience search tile pricing',
                          })
                        }
                        size="L"
                        nonMemberPriceSize="L"
                      />
                    </CSSBreakpoint>
                  </div>
                  <CSSBreakpoint min="tablet">
                    {experience.discount >=
                      EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW && (
                      <Pill
                        kind="primary"
                        variant={isLuxPricingShown ? 'lux-plus' : 'success'}
                      >
                        {intl.formatMessage(
                          {
                            id: 'ctVC018ALkSWpzUuKHkt',
                            defaultMessage: 'Save up to {percentage}%',
                            description:
                            'Discount pill showing the savings percentage in experience search tile',
                          },
                          { percentage: experience.discount },
                        )}
                      </Pill>
                    )}
                  </CSSBreakpoint>
                </Group>
                <PriceRowAgentHubCommission
                  offerId={experience.id}
                  productType={productType}
                  offerLocations={[experience.location.name]}
                  offer={experience}
                />
              </PriceSectionLeft>
              <HoverCTA>
                <TextButton kind="primary" nonInteractable>
                  {intl.formatMessage({
                    id: 'w5q4E5KrjgjqEQSjX8VT',
                    defaultMessage: 'View',
                    description:
                    'Button text to view experience offer details on compact search tile',
                  })}
                </TextButton>
              </HoverCTA>
            </Group>
            <CSSBreakpoint max="mobile">
              {experience.discount >=
                EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW && (
                <div style={{ marginTop: rem(4) }}>
                  <Pill
                    kind="primary"
                    variant={isLuxPricingShown ? 'lux-plus' : 'success'}
                  >
                    {intl.formatMessage(
                      {
                        id: 'ctVC018ALkSWpzUuKHkt',
                        defaultMessage: 'Save up to {percentage}%',
                        description:
                        'Discount pill showing the savings percentage in experience search tile',
                      },
                      { percentage: experience.discount },
                    )}
                  </Pill>
                </div>
              )}
            </CSSBreakpoint>
          </div>
        </TileContent>
      </Tile>
    </TileWrapper>
  )
}

export default ExperienceCompactSearchTile
